import React, { Suspense } from 'react';
import {
  Redirect, Route, RouteComponentProps, Switch,
} from 'react-router-dom';
import { observer } from 'mobx-react';
import { useStore } from '../../store/mst';
import * as RoutePaths from '../paths';
import RouteLoadingFallback from '../components/RouteLoadingFallback';
import { routeLoaders } from '../routeLoaders';

// Lazy load components using React.lazy
const LazyNavRoute = React.lazy(routeLoaders.navRoute);
const LazyHome = React.lazy(routeLoaders.workspace);
const LazyCompanySettings = React.lazy(routeLoaders.companySettings);
const LazyAccountSettings = React.lazy(routeLoaders.accountSettings);
const LazyUpdatePassword = React.lazy(routeLoaders.updatePassword);
const LazyInsights = React.lazy(routeLoaders.insights);
const LazyInsightsDetails = React.lazy(routeLoaders.insightsDetails);
const LazyNotFound = React.lazy(routeLoaders.notFound);
const LazyDriverLandingPage = React.lazy(routeLoaders.driverLandingPage);
const LazyGdprCustomerDataReportPage = React.lazy(routeLoaders.gdprCustomerDataReportPage);

const App: React.FunctionComponent<RouteComponentProps> = () => {
  const store = useStore();

  if (store.account.isDriverOnly) {
    return (
      <Suspense fallback={<RouteLoadingFallback fullViewport />}>
        <Switch location={store.router.location}>
          <LazyNavRoute data-testid="home" exact path={RoutePaths.rootPath} component={LazyDriverLandingPage} />
          <LazyNavRoute data-testid="accountSettings" exact path={RoutePaths.accountSettingsPath} component={LazyAccountSettings} />
          <Redirect to={RoutePaths.rootPath} />
        </Switch>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<RouteLoadingFallback fullViewport />}>
      <Switch location={store.router.location}>
        {/* *****************************************************************
        * When using a path with a URL Param (ie. `:workspaceId`)
        * make sure to use `syncParamsWithStore(useParams(), store.router);` within the component
        * **************************************************************** */}
        <LazyNavRoute data-testid="home"
          path={[RoutePaths.workspaceIdPath]}
          component={LazyHome}
        />
        <LazyNavRoute data-testid="companySettings" exact path={RoutePaths.settingsPath} component={LazyCompanySettings} />
        <LazyNavRoute data-testid="accountSettings" exact path={RoutePaths.accountSettingsPath} component={LazyAccountSettings} />
        <LazyNavRoute data-testid="insights" exact path={RoutePaths.insightsPath} component={LazyInsights} />
        <LazyNavRoute data-testid="insightsDetails" exact path={RoutePaths.insightsDetailsPath} component={LazyInsightsDetails} />
        {/* Standalone (chromeless) landing page for the GDPR report email deep link. */}
        <Route exact path={RoutePaths.gdprCustomerDataReportPath} component={LazyGdprCustomerDataReportPage} />
        <Route exact path={RoutePaths.updatePasswordPath} component={LazyUpdatePassword} />
        <LazyNavRoute data-testid="notFound" path="*" component={LazyNotFound} />
      </Switch>
    </Suspense>
  );
};

export default observer(App);
